// ---------------------------------------------------------------------------
// VoicePicker — lets the user choose the navigation voice (real-drive note:
// "you should be able to choose the voice" / "the voice should be ... nicer").
//
// Tapping a voice selects it AND speaks a sample line, so the choice is heard
// immediately. "Automatic" hands the choice back to the app's best-available
// pick. The note at the bottom steers the user toward iOS's Enhanced/Premium
// voices — the genuine fix for a choppy, robotic-sounding voice, since the
// synthesis quality is the OS's to provide, not something the app can fake.
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { Check, Volume2 } from "lucide-react";
import {
  listEnglishVoices,
  getChosenVoiceId,
  setChosenVoiceId,
  speakNative,
  type PleasantVoice,
} from "@/utils/nativeVoice";

// A short turn instruction in the savorer voice — long enough to judge the
// voice by, short enough not to be annoying on repeat taps.
const SAMPLE = "In a quarter mile, take the right — it's the prettier way.";

const QUALITY_LABEL: Record<string, string> = {
  premium: "Premium",
  enhanced: "Enhanced",
  default: "Standard",
};

function rowClass(selected: boolean): string {
  return `w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border transition-smooth ${
    selected
      ? "border-primary/60 bg-pleasant/10"
      : "border-border bg-card hover:bg-muted/50"
  }`;
}

export const VoicePicker = () => {
  const [voices, setVoices] = useState<PleasantVoice[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(() => getChosenVoiceId());

  useEffect(() => {
    let cancelled = false;
    listEnglishVoices().then((v) => { if (!cancelled) setVoices(v); });
    return () => { cancelled = true; };
  }, []);

  const pick = (id: string | null) => {
    setChosenVoiceId(id);
    setChosen(id);
    // Speak a sample so the choice is heard right away.
    void speakNative(SAMPLE, { volume: 1 });
  };

  if (voices === null) {
    return <p className="text-xs text-muted-foreground">Loading voices…</p>;
  }

  // The single biggest lever on "the voice should be nicer": an Enhanced or
  // Premium iOS voice. When none is installed, say so prominently — the
  // footnote below is easy to miss, and a compact voice will sound robotic
  // no matter what the app does.
  const hasGoodVoice = voices.some((v) => v.quality === "premium" || v.quality === "enhanced");

  return (
    <div className="space-y-1.5">
      {voices.length > 0 && !hasGoodVoice && (
        <div className="rounded-xl border border-amber-300/60 bg-amber-50 px-3 py-2.5 text-[12px] leading-snug text-amber-900">
          <span className="font-medium">Your phone only has its basic voice installed.</span>{" "}
          For a much smoother navigation voice, open iOS Settings → Accessibility →
          Spoken Content → Voices → English and download a <span className="font-medium">Premium</span> voice
          (Ava or Evan are lovely). It appears here automatically — no app restart needed.
        </div>
      )}
      <button
        type="button"
        onClick={() => pick(null)}
        className={rowClass(chosen === null)}
      >
        <span className="flex flex-col text-left">
          <span className="text-sm font-medium">Automatic</span>
          <span className="text-[11px] text-muted-foreground">
            The best voice your phone has
          </span>
        </span>
        {chosen === null && <Check className="w-4 h-4 text-primary shrink-0" />}
      </button>

      {voices.map((v) => (
        <button
          key={v.id}
          type="button"
          onClick={() => pick(v.id)}
          className={rowClass(chosen === v.id)}
        >
          <span className="flex flex-col text-left min-w-0">
            <span className="text-sm font-medium truncate">{v.name}</span>
            <span className="text-[11px] text-muted-foreground">
              {QUALITY_LABEL[v.quality] ?? "Standard"}
            </span>
          </span>
          {chosen === v.id ? (
            <Check className="w-4 h-4 text-primary shrink-0" />
          ) : (
            <Volume2 className="w-4 h-4 text-muted-foreground/50 shrink-0" />
          )}
        </button>
      ))}

      {voices.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No installed voices found — your phone will use its own default.
        </p>
      )}

      <p className="text-[11px] text-muted-foreground/80 pt-1.5 leading-snug">
        Want a smoother, less robotic voice? Add an Enhanced or Premium voice
        in iOS Settings → Accessibility → Spoken Content → Voices. New voices
        appear here on their own.
      </p>
    </div>
  );
};
